/**
 * Application Routing Module
 * 
 * Central routing configuration for the MacSafety web application. Defines all
 * application routes, their authentication requirements, and access control.
 * Routes are organized by functionality with appropriate guards applied for
 * security and access control.
 * 
 * Route Categories:
 * - Public Routes: Landing pages accessible to all users
 * - Protected Routes: Forms and form submission requiring user authentication
 * - Admin Routes: Form entries and viewing requiring both authentication and admin privileges
 * 
 * Authentication & Authorization Guards:
 * - MsalGuard: Ensures user is authenticated via Microsoft Entra ID (Azure AD)
 * - FormAccessGuard: Ensures user has permission to access specific forms
 * - AdminGuard: Ensures authenticated user belongs to admin user group
 * - AuthRedirectGuard: Handles redirects for authenticated users on public routes
 * 
 * Route Structure:
 * - /: Public landing page with authentication redirect logic
 * - /landing: Explicit landing page route
 * - /forms: Protected forms section with dynamic form routes
 * - /form-entries: Admin-only form entry listing
 * - /form-view/:id: Admin-only individual form entry viewing
 * - /form-submission-confirmation/:formType: Protected form submission confirmation
 * 
 * Dynamic Route Generation:
 * - Form routes are generated automatically from FORM_REGISTRY
 * - Each form gets its own route with metadata (title, icon, form type)
 * - All form routes inherit authentication protection from parent /forms route
 * - Individual form routes get FormAccessGuard for permission checking
 * 
 * Access Control Flow:
 * 1. MsalGuard checks authentication (applied to parent /forms route)
 * 2. FormAccessGuard checks form-specific permissions (applied to each form route)
 * 3. Users without access are redirected to /forms landing page
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */

import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { FormEntriesComponent } from './components/forms/form-entries/form-entry-list/form-entries.component';
import { FormsLandingComponent } from './components/landing-page/logged-in-landing/forms-landing.component';
import { LandingPageComponent } from './components/landing-page/logged-out-landing/landing-page.component';
import { FormViewComponent } from './components/forms/form-entries/form-entry-view/form-view.component';
import { MsalGuard } from '@azure/msal-angular';
import { AuthRedirectGuard } from './guards/auth-redirect.guard';
import { AdminGuard } from './guards/admin.guard';
import { FormAccessGuard } from './guards/form-access.guard';
import { FORM_REGISTRY } from './models/form-registry';
import { SubmissionConfirmationComponent } from './components/forms/form-submission/submission-confirmation/form-submission-confirmation.component';

/**
 * Generates form routes dynamically based on the FORM_REGISTRY.
 * Each route is protected by both MsalGuard (authentication) and FormAccessGuard (authorization).
 * Creates individual routes for each form type with appropriate metadata.
 * 
 * Guard Order:
 * 1. MsalGuard (inherited from parent /forms route) - checks authentication
 * 2. FormAccessGuard (applied to each form route) - checks form-specific permissions
 * 
 * @returns Array of route configurations for form components
 */
function generateFormRoutes(): Routes {
  return Object.entries(FORM_REGISTRY).map(([formType, metadata]) => ({
    path: formType,
    component: metadata.component,
    // Add FormAccessGuard to check form-specific permissions
    canActivate: [FormAccessGuard],
    data: {
      title: metadata.title,
      formType: formType, // Required by FormAccessGuard
      titleIcon: metadata.icon
    }
  }));
}

/**
 * Main application routes configuration
 * Routes are organized by access level and functionality
 */
const routes: Routes = [
  // Main landing page - accessible to all users, but redirects authenticated users
  // AuthRedirectGuard handles redirecting logged-in users to appropriate landing
  { 
    path: '', 
    component: LandingPageComponent, 
    canActivate: [AuthRedirectGuard],
    data: { title: 'Welcome to MacSafety' }
  },
  
  // Landing page explicit route (for clarity and direct access)
  // Same behavior as root route - redirects authenticated users
  { 
    path: 'landing', 
    component: LandingPageComponent, 
    canActivate: [AuthRedirectGuard],
    data: { title: 'Welcome to MacSafety' }
  },

   // Form entries listing - requires both authentication and admin privileges
  // AdminGuard ensures user belongs to admin user group
  { 
    path: 'form-entries', 
    component: FormEntriesComponent, 
    canActivate: [MsalGuard, AdminGuard],
    data: { title: 'Form Entries' }
  },
  
  // Forms section - protected by authentication (MsalGuard)
  // All child routes inherit authentication requirement from parent
  {
    path: 'forms',
    canActivate: [MsalGuard], // Guard the parent route for all child routes
    data: { title: 'All Forms' },
    children: [
      // Forms landing page - shows available forms to authenticated users
      { 
        path: '', 
        component: FormsLandingComponent,
        data: { title: 'All Forms' }
        // MsalGuard not needed here since parent route already has it
        // FormAccessGuard not needed - landing page shows only accessible forms
      },
      // Dynamic form routes - generated from FORM_REGISTRY
      // Each form gets its own route with metadata and access control
      // MsalGuard is inherited from parent, FormAccessGuard checks form permissions
      ...generateFormRoutes()
    ]
  },
  
 
  
  // Individual form entry viewing - requires both authentication and admin privileges
  // AdminGuard ensures only admin users can view submitted form data
  { 
    path: 'form-view/:id', 
    component: FormViewComponent, 
    canActivate: [MsalGuard, AdminGuard],
    data: { title: 'View Form Entry' }
  },
  
  // Form submission confirmation - requires authentication only
  // Users can view their own submission confirmations
  // Note: This could potentially use FormAccessGuard if you want to restrict
  // viewing confirmations to users who have access to the form type
  {
    path: 'form-submission-confirmation/:formType',
    component: SubmissionConfirmationComponent,
    canActivate: [MsalGuard],
    data: { title: 'Form Submission Confirmation' }
  },
  
  // Catch all route - redirect to landing page for any unmatched routes
  // Provides graceful fallback for invalid URLs
  { 
    path: '**', 
    redirectTo: '/landing' 
  }
];

/**
 * Main routing module configuration
 * Configures router with performance optimizations and debugging options
 */
@NgModule({
  imports: [RouterModule.forRoot(routes, {
    // Prevent unnecessary route re-evaluation when navigating to same URL
    onSameUrlNavigation: 'ignore',
    // Enable route tracing for debugging (set to true only when needed)
    enableTracing: false
  })],
  exports: [RouterModule],
})
export class AppRoutingModule { }